import { PaymentReportItem } from "@/services/PaymentReportService";

export interface ReportData {
  type: "Etransfer" | "Stripe" | "Cheque";
  items: PaymentReportItem[];
}

export class PaymentReportExcelGenerator {
  /**
   * Generate CSV files for all payment types
   * Creates separate CSV files for each payment type
   */
  static generateExcelFile(
    reports: ReportData[],
    startDate: string,
    endDate: string
  ): void {
    reports.forEach((report) => {
      const csvContent = this.formatDataForCSV(report.type, report.items);
      const filename = `Payment_Report_${report.type}_${startDate}_to_${endDate}.csv`;
      this.downloadCSV(csvContent, filename);
    });
  }

  /**
   * Format data for CSV based on payment type
   */
  private static formatDataForCSV(
    type: "Etransfer" | "Stripe" | "Cheque",
    items: PaymentReportItem[]
  ): string {
    let headers: string[];
    let rows: string[];

    if (type === "Cheque") {
      headers = [
        "Customer Number",
        "Name",
        "Cheque Number",
        "Invoice Number",
        "Amount",
        "Department",
      ];
      rows = items.map((item) =>
        [
          item.customerID,
          this.escapeCSVValue(item.customer_name),
          this.escapeCSVValue(item.referenceNumber || ""),
          item.mrID,
          item.amount,
          "Service",
        ].join(",")
      );
    } else if (type === "Stripe") {
      headers = [
        "Customer Number",
        "Name",
        "Invoice Number",
        "Amount",
        "Fee",
        "Net",
        "Date Paid",
        "Available On",
      ];
      rows = items.map((item) =>
        [
          item.customerID,
          this.escapeCSVValue(item.customer_name),
          item.mrID,
          item.amount,
          item.fee ?? "",
          item.net ?? "",
          this.escapeCSVValue(item.stripe_created || ""),
          this.escapeCSVValue(item.available_on || ""),
        ].join(",")
      );
    } else {
      // Etransfer has the same structure as before
      headers = ["Customer Number", "Name", "Invoice Number", "Amount"];
      rows = items.map((item) =>
        [
          item.customerID,
          this.escapeCSVValue(item.customer_name),
          item.mrID,
          item.amount,
        ].join(",")
      );
    }

    return [headers.join(","), ...rows].join("\n");
  }

  /**
   * Escape CSV values (handle commas, quotes, newlines)
   */
  private static escapeCSVValue(value: string | number): string {
    if (value === null || value === undefined) {
      return "";
    }
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  /**
   * Download CSV file
   */
  private static downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Generate separate CSV files for each payment type (alias for generateExcelFile)
   */
  static generateSeparateFiles(
    reports: ReportData[],
    startDate: string,
    endDate: string
  ): void {
    this.generateExcelFile(reports, startDate, endDate);
  }
}

